import { useState, useEffect } from "react";
import { MonetizationContext } from "./context/MonetizationContext";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import JoinTrip from "./pages/JoinTrip";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const FEATURES = [
  "Trip creation",
  "AI Itinerary",
  "Bookings",
  "Expenses",
  "Travel Journal",
  "Friends",
  "Local Tips",
  "Scratch Map",
  "General / Other",
];

function FeedbackWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feature, setFeature] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setOpen(false);
    setRating(0);
    setHoverRating(0);
    setFeature("");
    setMessage("");
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uid: user.uid,
        email: user.email,
        rating,
        feature,
        message,
        createdAt: serverTimestamp(),
        appVersion: "beta-1.0",
      });
      setSubmitted(true);
      setTimeout(() => close(), 2000);
    } catch (err) {
      console.error("Feedback submit error:", err);
    }
    setSubmitting(false);
  };

  return (
    <>
      {/* ── FLOATING BUTTON ── */}
      <button
        onClick={() => setOpen(true)}
        onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(30,45,74,0.4)"; }}
        onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(30,45,74,0.28)"; }}
        style={{
          position: "fixed",
          bottom: 88,
          right: 24,
          zIndex: 999,
          background: "#1E2D4A",
          color: "#FAF6F0",
          border: "none",
          borderRadius: 100,
          padding: "10px 18px",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "'DM Sans', sans-serif",
          cursor: "pointer",
          boxShadow: "0 4px 20px rgba(30,45,74,0.28)",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "transform 0.15s, box-shadow 0.15s",
        }}
      >
        💬 Feedback
      </button>

      {/* ── MODAL ── */}
      {open && (
        <div
          onClick={e => { if (e.target === e.currentTarget) close(); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(26,23,20,0.52)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div style={{
            background: "white",
            borderRadius: 24,
            padding: "32px 28px",
            width: "100%",
            maxWidth: 460,
            position: "relative",
            boxShadow: "0 24px 80px rgba(26,23,20,0.22)",
          }}>
            {/* Close */}
            <button
              onClick={close}
              style={{
                position: "absolute", top: 16, right: 16,
                background: "none", border: "none",
                fontSize: 20, color: "rgba(26,23,20,0.38)",
                cursor: "pointer", lineHeight: 1, padding: 4,
              }}
            >×</button>

            {submitted ? (
              /* ── THANK YOU STATE ── */
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>☀️</div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 22, fontWeight: 600, color: "#1a1714", marginBottom: 8,
                }}>Thank you!</div>
                <div style={{ fontSize: 14, color: "rgba(26,23,20,0.55)", fontWeight: 300, lineHeight: 1.55 }}>
                  Your feedback helps us build a better Solis.
                </div>
              </div>
            ) : (
              /* ── FORM ── */
              <>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 22, fontWeight: 600, color: "#1a1714",
                  letterSpacing: "-0.02em", marginBottom: 4,
                }}>
                  How's Solis treating you?
                </h2>
                <p style={{ fontSize: 13, color: "rgba(26,23,20,0.5)", fontWeight: 300, marginBottom: 24, lineHeight: 1.5 }}>
                  Takes 30 seconds. Helps us a lot. ☀️
                </p>

                {/* Q1 — Stars */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(26,23,20,0.55)", marginBottom: 10 }}>
                    Overall experience so far
                  </div>
                  <div style={{ display: "flex", gap: 2 }}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        onMouseEnter={() => setHoverRating(n)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(n)}
                        style={{
                          fontSize: 34,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: n <= (hoverRating || rating) ? "#F5A623" : "#1E2D4A",
                          opacity: n <= (hoverRating || rating) ? 1 : 0.22,
                          transform: n <= hoverRating ? "scale(1.18)" : "scale(1)",
                          transition: "color 0.1s, transform 0.1s, opacity 0.1s",
                          padding: "0 3px",
                          lineHeight: 1,
                        }}
                      >★</button>
                    ))}
                  </div>
                </div>

                {/* Q2 — Feature dropdown */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(26,23,20,0.55)", marginBottom: 8 }}>
                    Which feature were you using?
                  </div>
                  <select
                    value={feature}
                    onChange={e => setFeature(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: "1.5px solid rgba(26,23,20,0.12)",
                      borderRadius: 10,
                      background: "#fafaf8",
                      fontSize: 13,
                      fontFamily: "'DM Sans', sans-serif",
                      color: feature ? "#1a1714" : "rgba(26,23,20,0.38)",
                      outline: "none",
                    }}
                  >
                    <option value="" disabled>Select a feature...</option>
                    {FEATURES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>

                {/* Q3 — Open text */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(26,23,20,0.55)", marginBottom: 8 }}>
                    Anything broken or worth improving?
                  </div>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={4}
                    placeholder="Be honest — we can take it 😄"
                    onFocus={e => { e.target.style.borderColor = "#F5A623"; e.target.style.background = "white"; }}
                    onBlur={e => { e.target.style.borderColor = "rgba(26,23,20,0.12)"; e.target.style.background = "#fafaf8"; }}
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: "1.5px solid rgba(26,23,20,0.12)",
                      borderRadius: 10,
                      background: "#fafaf8",
                      fontSize: 13,
                      fontFamily: "'DM Sans', sans-serif",
                      color: "#1a1714",
                      outline: "none",
                      resize: "none",
                      lineHeight: 1.6,
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!rating || submitting}
                  style={{
                    width: "100%",
                    padding: 14,
                    background: rating ? "#F5A623" : "rgba(26,23,20,0.06)",
                    color: rating ? "#1E2D4A" : "rgba(26,23,20,0.28)",
                    border: "none",
                    borderRadius: 12,
                    fontSize: 15,
                    fontWeight: 500,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: rating ? "pointer" : "not-allowed",
                    transition: "all 0.2s",
                  }}
                >
                  {submitting ? "Sending…" : "Send feedback ☀️"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [joinTripId, setJoinTripId] = useState(null);
  const [joinTripToken, setJoinTripToken] = useState(null);
  const [pendingJoinTrip, setPendingJoinTrip] = useState(null);
  const [premiumGateEnabled, setPremiumGateEnabled] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/join\/(.+)$/);
    if (match) {
      setJoinTripId(match[1]);
      const token = new URLSearchParams(window.location.search).get("token");
      setJoinTripToken(token);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const [snap, cfgSnap] = await Promise.all([
          getDoc(doc(db, "users", u.uid)),
          getDoc(doc(db, "appConfig", "monetization")).catch((e) => {
            console.error("appConfig read failed; defaulting premium gate OFF", e);
            return null;
          }),
        ]);
        const d = snap.data();
        setHasProfile(
          snap.exists() &&
          !!d?.name &&
          !!d?.travelStyle &&
          !!d?.pace &&
          !!d?.food &&
          !!d?.budget
        );
        setPremiumGateEnabled(cfgSnap !== null && cfgSnap.exists() && cfgSnap.data().premiumGateEnabled === true);

        const stored = localStorage.getItem("pendingTripJoin");
        if (stored) {
          localStorage.removeItem("pendingTripJoin");
          try {
            const parsed = JSON.parse(stored);
            setJoinTripId(parsed.tripId);
            setJoinTripToken(parsed.token || null);
          } catch (_) {
            // old format was a plain tripId string
            setJoinTripId(stored);
          }
        }
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const renderPage = () => {
    if (loading) return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#FAF6F0",
        fontFamily: "DM Sans, sans-serif",
        color: "rgba(26,23,20,0.4)",
        fontSize: 14,
      }}>
        Loading...
      </div>
    );

    if (joinTripId) return (
      <JoinTrip
        tripId={joinTripId}
        inviteToken={joinTripToken}
        onJoined={(trip) => {
          if (!trip) {
            console.error("App.onJoined called with null trip — JoinTrip race condition or upstream bug");
            return;
          }
          setPendingJoinTrip(trip);
          setJoinTripId(null);
          setJoinTripToken(null);
          window.history.pushState({}, "", "/");
        }}
        onAuth={() => {
          localStorage.setItem("pendingTripJoin", JSON.stringify({ tripId: joinTripId, token: joinTripToken }));
          setJoinTripId(null);
          setJoinTripToken(null);
        }}
      />
    );

    if (!user) return <Auth />;

    if (!hasProfile) return (
      <Profile onComplete={() => setHasProfile(true)} />
    );

    return (
      <Dashboard
        pendingJoinTrip={pendingJoinTrip}
        onPendingJoinHandled={() => setPendingJoinTrip(null)}
      />
    );
  };

  return (
    <MonetizationContext.Provider value={{ premiumGateEnabled }}>
      {renderPage()}
      {user && <FeedbackWidget user={user} />}
    </MonetizationContext.Provider>
  );
}
